import * as ngCore from '@angular/core'
// eslint-disable-next-line @typescript-eslint/naming-convention
import type * as Monaco from 'monaco-editor'
import * as rxjs from 'rxjs'

import { MonacoManager } from './monaco-manager'

/**
 * Monaco is a full editor with workers and language code, so importing it like
 * a normal library would put its ESM runtime in the initial Angular bundle.
 * Instead, we load the AMD assets from `/assets/monaco/min/vs` on demand.
 *
 * The type-only import above gives TypeScript Monaco types and emits no
 * JavaScript. Runtime code uses the AMD-created global `monaco`.
 */
interface MonacoRequire {
  (dependencies: string[], callback: () => void): void
  config(options: { paths: { vs: string } }): void
}

/**
 * Describes AMD-created globals for TypeScript only; emits no JavaScript.
 *
 * `const monaco` types the runtime global. `namespace monaco` keeps existing
 * annotations like `monaco.editor.ITextModel` working. `Window` types the
 * loader checks in {@link MonacoManagerService._load}.
 */
declare global {
  /* eslint-disable @typescript-eslint/naming-convention */
  const monaco: typeof Monaco

  namespace monaco {
    export type IPosition = Monaco.IPosition
    export type IRange = Monaco.IRange
    export type Position = Monaco.Position

    export namespace editor {
      export type IActionDescriptor = Monaco.editor.IActionDescriptor
      export type IEditorOptions = Monaco.editor.IEditorOptions
      export type IGlobalEditorOptions = Monaco.editor.IGlobalEditorOptions
      export type IStandaloneCodeEditor = Monaco.editor.IStandaloneCodeEditor
      export type ITextModel = Monaco.editor.ITextModel
      export type IWordAtPosition = Monaco.editor.IWordAtPosition
    }

    export namespace languages {
      export type CompletionContext = Monaco.languages.CompletionContext
      export type CompletionItem = Monaco.languages.CompletionItem
      export type CompletionList = Monaco.languages.CompletionList
      export type DocumentFormattingEditProvider =
        Monaco.languages.DocumentFormattingEditProvider
      export type IMonarchLanguage = Monaco.languages.IMonarchLanguage
      export type LanguageConfiguration = Monaco.languages.LanguageConfiguration
    }
  }

  interface Window {
    monaco?: typeof Monaco
    require?: MonacoRequire
  }
  /* eslint-enable @typescript-eslint/naming-convention */
}

// TODO: go back to relative path when this is fixed:
// https://github.com/microsoft/monaco-editor/issues/4778
const MONACO_VS_BASE_URL = `${window.location.origin}/assets/monaco/min/vs`

/**
 * Setting up Monaco Editor is a little tricky
 *
 * Here are some relevant resources:
 * - https://github.com/microsoft/monaco-editor/tree/main/samples
 * - https://stackoverflow.com/questions/71072724/implement-monaco-editor-in-angular-13
 */
@ngCore.Injectable({ providedIn: 'root' })
export class MonacoManagerService {
  /**
   * Emits an emission as soon as the Monaco dependencies have been loaded
   */
  private _loaded$: rxjs.Observable<void> | undefined

  /**
   * Runs the given callback after the Monaco Editor has been loaded
   */
  public createManager(): MonacoManager {
    this._loaded$ ??= this._load()
    return new MonacoManager(this._loaded$)
  }

  /**
   * Loads the necessary Monaco dependencies
   */
  private _load(): rxjs.Observable<void> {
    const globals = window as ExpectedWindow
    // NOTE: ReplaySubject ensures that subscribers that subscribe after loading
    // is completed still get an emission
    const loaded$ = new rxjs.ReplaySubject<void>(1)
    if (typeof globals.monaco === 'object') {
      // If Monaco has already been set up, there is nothing else we need to do
      loaded$.next()
      return loaded$
    }

    /**
     * Loads the Monaco Editor
     *
     * Assumes that `require` already exists
     */
    function loadMonacoEditor() {
      if (!globals.require) {
        console.warn(
          'Something went wrong in loading the Monaco Editor. `window.require` is `undefined`',
        )
        return
      }
      globals.require.config({ paths: { vs: MONACO_VS_BASE_URL } })
      globals.require([`vs/editor/editor.main`], () => {
        loaded$.next()
      })
    }

    if (globals.require === undefined) {
      // If we do not have a global AMD require, we first need to load it
      const loaderScript: HTMLScriptElement = document.createElement('script')
      loaderScript.type = 'text/javascript'
      loaderScript.src = `${MONACO_VS_BASE_URL}/loader.js`
      // And after it is loaded, we can proceed with using it to loadMonacoEditor
      loaderScript.addEventListener('load', loadMonacoEditor)
      document.body.appendChild(loaderScript)
    } else {
      // Otherwise, we can proceed with using it to loadMonacoEditor immediately
      loadMonacoEditor()
    }

    return loaded$
  }
}

/**
 * Represents the expected properties from `window` that are used in
 * {@link MonacoManagerService._load}
 */
type ExpectedWindow = Partial<typeof window> & {
  monaco?: typeof Monaco
  require?: MonacoRequire
}
